<template>
  <div class="leak-demo-wrapper w-full max-w-lg mx-auto">
    <!-- Simple container with border -->
    <div class="app-container bg-white rounded-lg border-2 border-gray-200 overflow-hidden shadow-lg">
      <!-- Content area -->
      <div class="app-content p-4 min-h-[280px]">
        <TransitionGroup
          name="leak"
          tag="div"
          class="space-y-2"
        >
          <div
            v-for="(leak, index) in visibleLeaks"
            :key="leak.id"
            :data-leak-id="leak.id"
            :data-is-live="false"
            class="leak-item bg-white rounded-md lg:rounded-none lg:border-b border-gray-200 mb-2 lg:mb-0 relative hover:bg-gray-accent"
          >
            <div class="block text-sm">
              <!-- Mobile & Medium View (stacked) -->
              <div class="p-2">
                <div class="flex items-center mb-1.5">
                  <img
                    :src="leak.favicon"
                    :alt="`${leak.title} favicon`"
                    class="w-4 h-4 mr-2 flex-shrink-0"
                  >
                  <div class="truncate">
                    <p class="font-semibold text-gray-800 truncate text-sm" :title="leak.title">{{ leak.title }}</p>
                    <p class="text-gray-500 truncate text-xs" :title="leak.url">{{ leak.url }}</p>
                  </div>
                </div>
                <div class="text-xs text-gray-600 mb-1">
                  <span v-if="leak.foundOn === 'website'">{{ translations.foundOnWebsite }}</span>
                  <span v-else class="flex items-center">
                    {{ translations.foundThrough }} 
                    <img
                      :src="googleIcon"
                      alt="Google Images"
                      class="w-3 h-3 ml-1 mr-0.5 flex-shrink-0"
                    >
                    Google Images
                  </span>
                </div>
                <div class="text-xs text-gray-500 mb-1.5">{{ leak.date }}</div>
                <div class="flex flex-wrap items-center gap-2">
                  <span
                    class="text-xs font-semibold px-1.5 py-0.5 rounded-full"
                    style="background-color: rgb(0, 0, 0); color: rgb(255, 255, 255);"
                  >
                    {{ translations.found }}
                  </span>
                  <span class="text-xs text-gray-600">{{ leak.removalType }}</span>
                </div>
              </div>
            </div>
          </div>
        </TransitionGroup>
        
        <!-- Loading indicator for remaining items -->
        <div v-if="leakIndex < leaks.length" class="mt-3 text-center">
          <div class="inline-flex items-center text-xs text-gray-500">
            <svg class="animate-spin h-3 w-3 mr-2" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            {{ translations.scanning }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'LeakAnimation',
  data() {
    return {
      visibleLeaks: [],
      leakIndex: 0,
      intervalId: null,
      leaks: [],
      translations: {}
    };
  },
  props: {
    locale: {
      type: String,
      default: 'en'
    }
  },
  computed: {
    googleIcon() {
      return '/build/images/search-engines/google.png';
    }
  },
  mounted() {
    // Initialize translations
    this.initializeTranslations();
    
    // Initialize leaks data
    this.initializeLeaks();
    
    // Start animation after a short delay
    setTimeout(() => {
      this.startAnimation();
    }, 500);
  },
  beforeUnmount() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
  },
  methods: {
    initializeTranslations() {
      this.translations = {
        found: this.locale === 'de' ? 'Gefunden' : 'Found',
        foundOnWebsite: this.locale === 'de' ? 'Auf Website gefunden' : 'Found on Website',
        foundThrough: this.locale === 'de' ? 'Gefunden über' : 'Found through',
        directRemoval: this.locale === 'de' ? 'Direkte Website-Entfernung' : 'Direct Website Removal',
        searchEngineRemoval: this.locale === 'de' ? 'Entfernung bei Suchmaschine' : 'Removal on Search Engine',
        scanning: this.locale === 'de' ? 'Weitere Leaks werden gescannt...' : 'Scanning for more leaks...'
      };
    },
    initializeLeaks() {
      const today = new Date();
      const todayString = this.locale === 'de' 
        ? today.toLocaleDateString('de-DE') 
        : today.toLocaleDateString('en-US');
      
      const titles = this.locale === 'de' ? [
        'Jane Doe Nacktbilder OnlyFans Foto #30 – Leaked Paradise',
        'Jane Doe private Bilder durchgesickert – Nudogram v2.1'
      ] : [
        'Jane Doe Nude OnlyFans Photo #30 – The Fappening Plus!',
        'Jane Doe Nude Leaks OnlyFans Photo #79 - Nudogram v2.1'
      ];
      
      this.leaks = [
        {
          id: 'DEMO-001',
          title: titles[0],
          url: 'https://thefappening.plus/photos/m/a/jane-doe/1000/jane-doe_0030.jpg',
          favicon: 'https://www.google.com/s2/favicons?domain=https%3A%2F%2Fthefappening.plus',
          foundOn: 'website',
          date: todayString,
          removalType: this.translations.directRemoval
        },
        {
          id: 'DEMO-002',
          title: titles[1],
          url: 'https://nudogram.com/contents/m/a/jane-doe/1000/jane-doe_0079.jpg',
          favicon: 'https://www.google.com/s2/favicons?domain=https%3A%2F%2Fnudogram.com',
          foundOn: 'google',
          date: todayString,
          removalType: this.translations.searchEngineRemoval
        }
      ];
    },
    startAnimation() {
      // Add first leak immediately
      this.addNextLeak();
      
      // Add remaining leaks with interval
      this.intervalId = setInterval(() => {
        if (this.leakIndex < this.leaks.length) {
          this.addNextLeak();
        } else {
          clearInterval(this.intervalId);
        }
      }, 800); // Faster interval - 0.8 seconds between each leak
    },
    addNextLeak() {
      if (this.leakIndex < this.leaks.length) {
        this.visibleLeaks.push(this.leaks[this.leakIndex]);
        this.leakIndex++;
      }
    }
  }
};
</script>

<style scoped>
/* Simple container styling */
.app-container {
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

/* Transition animations - smooth slide in from right */
.leak-enter-active {
  transition: all 0.8s cubic-bezier(0.4, 0, 0.2, 1);
}

.leak-leave-active {
  transition: all 0.3s ease-in;
}

.leak-enter-from {
  opacity: 0;
  transform: translateX(100px);
}

.leak-enter-to {
  opacity: 1;
  transform: translateX(0);
}

.leak-leave-to {
  opacity: 0;
  transform: translateX(-20px);
}

/* Move animation for existing items when new ones are added */
.leak-move {
  transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Keep original leak item styling exactly as in app */
.leak-item {
  transition: background-color 0.2s ease;
}

/* Responsive adjustments */
@media (max-width: 640px) {
  .leak-demo-wrapper {
    padding: 0 1rem;
  }
  
  .app-content {
    padding: 1rem !important;
    min-height: 200px !important;
  }
}
</style>